import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ServiceRequestItem } from '../../../../api/repairAndInquiries'
import { REPAIR_AND_INQUIRIES_STATUSES as STATUSES } from '../../../../constants/repairAndInquiriesStatuses'
import { RequestsList } from './'
import { FilterByStatus } from './FilterByStatus'

export { REPAIR_AND_INQUIRIES_STATUSES as STATUSES } from '../../../../constants/repairAndInquiriesStatuses'

export const Multipanel = ({
  totalRequests,
  visibleCount,
  activeStatus,
  setActiveStatus,
  loadingMore,
  onLoadMore
}: {
  totalRequests: ServiceRequestItem[],
  visibleCount: number,
  activeStatus: number,
  setActiveStatus: any,
  loadingMore?: boolean,
  onLoadMore: () => void
}) => {
  const total = totalRequests
  const { t } = useTranslation()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const onLoadMoreRef = useRef(onLoadMore)
  const loadingMoreRef = useRef(loadingMore)
  useEffect(() => { onLoadMoreRef.current = onLoadMore })
  useEffect(() => { loadingMoreRef.current = loadingMore }, [loadingMore])

  // Filter from the full list so e.g. "In progress" shows all matching tickets,
  // not just those that happen to be in the first sliced page.
  const filteredAll = activeStatus === 0
    ? total
    : (() => {
        const status = STATUSES.find(s => s.id === activeStatus)
        return status ? total.filter(r => status.nameAims.includes(r.status)) : total
      })()
  const filteredRequests = filteredAll.slice(0, visibleCount)
  const showLoadMore = visibleCount < filteredAll.length

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !showLoadMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) onLoadMoreRef.current()
      },
      { rootMargin: '120px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [showLoadMore])

  return (
    <div className='requests-multipanel-wrapper'>
      <FilterByStatus
        statuses={STATUSES}
        activeStatus={activeStatus}
        setActiveStatus={setActiveStatus}
        requests={total}
      />
      {filteredRequests.length === 0 && activeStatus !== 0 && (
        <div className='flex flex-col items-center justify-center py-20 text-center'>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#e8f4fc', display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 5.55228 9.44772 6 10 6H14C14.5523 6 15 5.55228 15 5M9 5C9 4.44772 9.44772 4 10 4H14C14.5523 4 15 4.44772 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="#218cda" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <p style={{ color: '#212121', fontFamily: '"Red Hat Display"', fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>
            {t('REPAIR_AND_INQUIRIES.CURRENTLY_NO_REQUESTS')}
          </p>
          <p style={{ color: '#9E9E9E', fontFamily: '"Red Hat Display"', fontSize: '0.875rem', fontWeight: 400 }}>
            {t('REPAIR_AND_INQUIRIES.NO_REQUESTS_FOR_FILTER')}
          </p>
        </div>
      )}
      <div className='multipanel-main'>
        <RequestsList
          statuses={STATUSES}
          requests={filteredRequests}
        />
        {showLoadMore && (
          <div ref={sentinelRef} style={{ minHeight: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px 0' }}>
            {loadingMore && (
              <>
                <div
                  style={{
                    width: 24, height: 24,
                    border: '3px solid #e8f4fc',
                    borderTopColor: '#218cda',
                    borderRadius: '50%',
                    animation: 'mp-spin 0.8s linear infinite',
                  }}
                />
                <style>{`@keyframes mp-spin { to { transform: rotate(360deg); } }`}</style>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
